import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Scale, Beaker, TrendingUp, Users, MousePointerClick, 
  Calendar, ArrowRight, Target
} from "lucide-react";
import { startOfDay, startOfWeek, startOfMonth, subMonths, format, eachMonthOfInterval, startOfMonth as getStartOfMonth, endOfMonth } from "date-fns";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

interface PartnerPerformanceKPIsProps {
  className?: string;
}

export function PartnerPerformanceKPIs({ className }: PartnerPerformanceKPIsProps) {
  const today = startOfDay(new Date());
  const weekStart = startOfWeek(today);
  const monthStart = startOfMonth(today);

  // Fetch consultation leads with Orderly/Nuda tracking
  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['partner-leads'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consultation_leads')
        .select('created_at, location_id, orderly_clicked, nuda_clicked')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch NudaVitae assessments
  const { data: nudaAssessments = [] } = useQuery({
    queryKey: ['nuda-assessments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('nudavitae_assessments')
        .select('id, created_at, status')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Filter by date helper
  const filterByDate = <T extends { created_at: string | null }>(items: T[], startDate: Date) => {
    return items.filter(item => 
      item.created_at && new Date(item.created_at) >= startDate
    );
  };

  // Partner click metrics
  const orderlyClicks = leads.filter(l => l.orderly_clicked).length;
  const nudaClicks = leads.filter(l => l.nuda_clicked).length;
  const totalLeads = leads.length;

  // Time-based metrics
  const orderlyClicksToday = filterByDate(leads.filter(l => l.orderly_clicked), today).length;
  const orderlyClicksWeek = filterByDate(leads.filter(l => l.orderly_clicked), weekStart).length;
  const orderlyClicksMonth = filterByDate(leads.filter(l => l.orderly_clicked), monthStart).length;

  const nudaClicksToday = filterByDate(leads.filter(l => l.nuda_clicked), today).length;
  const nudaClicksWeek = filterByDate(leads.filter(l => l.nuda_clicked), weekStart).length;
  const nudaClicksMonth = filterByDate(leads.filter(l => l.nuda_clicked), monthStart).length;

  // NudaVitae assessment metrics
  const assessmentsToday = filterByDate(nudaAssessments, today).length;
  const assessmentsWeek = filterByDate(nudaAssessments, weekStart).length;
  const assessmentsMonth = filterByDate(nudaAssessments, monthStart).length;
  const assessmentsTotal = nudaAssessments.length;

  // Calculate conversion rates
  const orderlyConversionRate = totalLeads > 0 
    ? ((orderlyClicks / totalLeads) * 100).toFixed(1) 
    : '0.0';
  const nudaConversionRate = totalLeads > 0 
    ? ((nudaClicks / totalLeads) * 100).toFixed(1) 
    : '0.0';
  const nudaAssessmentRate = nudaClicks > 0 
    ? ((assessmentsTotal / nudaClicks) * 100).toFixed(1) 
    : '0.0';

  // Monthly trend data for last 6 months
  const sixMonthsAgo = subMonths(new Date(), 6);
  const months = eachMonthOfInterval({ start: sixMonthsAgo, end: new Date() });
  
  const trendData = months.map(month => {
    const monthStartDate = getStartOfMonth(month);
    const monthEndDate = endOfMonth(month);
    
    const orderlyCount = leads.filter(l => {
      if (!l.orderly_clicked || !l.created_at) return false;
      const created = new Date(l.created_at);
      return created >= monthStartDate && created <= monthEndDate;
    }).length;
    
    const nudaCount = leads.filter(l => {
      if (!l.nuda_clicked || !l.created_at) return false;
      const created = new Date(l.created_at);
      return created >= monthStartDate && created <= monthEndDate;
    }).length;
    
    return {
      month: format(month, 'MMM'),
      orderly: orderlyCount,
      nuda: nudaCount,
    };
  });

  // Pie chart data
  const pieData = [
    { name: 'OrderlyMeds', value: orderlyClicks, color: '#f97316' },
    { name: 'Nuda Labs', value: nudaClicks, color: '#8b5cf6' },
    { name: 'Neither', value: Math.max(0, totalLeads - orderlyClicks - nudaClicks + 
      leads.filter(l => l.orderly_clicked && l.nuda_clicked).length), color: '#6b7280' },
  ].filter(d => d.value > 0);

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading partner analytics...</div>;
  }

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Partner Performance
              </CardTitle>
              <CardDescription>
                Nuda Labs vs OrderlyMeds funnel metrics
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">
              Lead Funnel Tracking
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Top-Level KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200 dark:border-orange-800">
              <Scale className="h-8 w-8 text-orange-600 dark:text-orange-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{orderlyClicks}</div>
              <div className="text-sm font-medium text-orange-700 dark:text-orange-300">OrderlyMeds Clicks</div>
              <div className="text-xs text-muted-foreground mt-1">{orderlyConversionRate}% of leads</div>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
              <Beaker className="h-8 w-8 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{nudaClicks}</div>
              <div className="text-sm font-medium text-purple-700 dark:text-purple-300">Nuda Labs Clicks</div>
              <div className="text-xs text-muted-foreground mt-1">{nudaConversionRate}% of leads</div>
            </div>
            <div className="text-center p-4 bg-violet-50 dark:bg-violet-950 rounded-lg border border-violet-200 dark:border-violet-800">
              <Users className="h-8 w-8 text-violet-600 dark:text-violet-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-violet-600 dark:text-violet-400">{assessmentsTotal}</div>
              <div className="text-sm font-medium text-violet-700 dark:text-violet-300">NudaVitae Assessments</div>
              <div className="text-xs text-muted-foreground mt-1">{nudaAssessmentRate}% completion</div>
            </div>
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
              <MousePointerClick className="h-8 w-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{totalLeads}</div>
              <div className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Leads</div>
              <div className="text-xs text-muted-foreground mt-1">All consultation requests</div>
            </div>
          </div>

          {/* Time Period Breakdown Table */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Partner Engagement Timeline
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium">Partner</th>
                    <th className="text-center py-2 font-medium">Today</th>
                    <th className="text-center py-2 font-medium">This Week</th>
                    <th className="text-center py-2 font-medium">This Month</th>
                    <th className="text-center py-2 font-medium">All Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-muted/50">
                    <td className="py-3 flex items-center gap-2">
                      <Scale className="h-4 w-4 text-orange-500" />
                      <span className="font-medium">OrderlyMeds</span>
                    </td>
                    <td className="text-center py-3 font-semibold">{orderlyClicksToday}</td>
                    <td className="text-center py-3 font-semibold">{orderlyClicksWeek}</td>
                    <td className="text-center py-3 font-semibold">{orderlyClicksMonth}</td>
                    <td className="text-center py-3 font-semibold">{orderlyClicks}</td>
                  </tr>
                  <tr className="border-b hover:bg-muted/50">
                    <td className="py-3 flex items-center gap-2">
                      <Beaker className="h-4 w-4 text-purple-500" />
                      <span className="font-medium">Nuda Labs</span>
                    </td>
                    <td className="text-center py-3 font-semibold">{nudaClicksToday}</td>
                    <td className="text-center py-3 font-semibold">{nudaClicksWeek}</td>
                    <td className="text-center py-3 font-semibold">{nudaClicksMonth}</td>
                    <td className="text-center py-3 font-semibold">{nudaClicks}</td>
                  </tr>
                  <tr className="hover:bg-muted/50">
                    <td className="py-3 flex items-center gap-2">
                      <Users className="h-4 w-4 text-violet-500" />
                      <span className="font-medium">NudaVitae Assessments</span>
                    </td>
                    <td className="text-center py-3 font-semibold">{assessmentsToday}</td>
                    <td className="text-center py-3 font-semibold">{assessmentsWeek}</td>
                    <td className="text-center py-3 font-semibold">{assessmentsMonth}</td>
                    <td className="text-center py-3 font-semibold">{assessmentsTotal}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Trend Chart */}
            <div>
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                6-Month Partner Trend
              </h4>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))' 
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="orderly" name="OrderlyMeds" fill="#f97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="nuda" name="Nuda Labs" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Distribution Pie */}
            <div>
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <ArrowRight className="h-4 w-4" />
                Lead Distribution
              </h4>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Funnel Insight */}
          <div className="p-4 bg-gradient-to-r from-orange-50 to-purple-50 dark:from-orange-950/50 dark:to-purple-950/50 rounded-lg border border-muted">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-orange-500" />
                <span className="font-medium">GLP-1 Weight Loss</span>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">vs</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="flex items-center gap-2">
                <Beaker className="h-5 w-5 text-purple-500" />
                <span className="font-medium">Peptide Stacks</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Track which partner offering resonates more with your leads. OrderlyMeds handles GLP-1 weight loss, while Nuda Labs powers personalized peptide therapy stacks.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}